// Reviewer list filter: defaults, API query params, and display chips.
// Keys match the backend (back-herogrow utils/reviewerFilter.util.js).
// Plain functions with no store or router dependency, so the admin build can
// use them too. Spec: docs/specs/reviewer-filtering.md

export const INVOICE_STATUS_OPTIONS = [
  { value: "unpaid", label: "Unpaid" },
  { value: "paid", label: "Paid" },
  { value: "claimed paid", label: "Claimed paid" },
  { value: "claimed internal", label: "Claimed internally" },
  { value: "none", label: "No invoice" },
];

// Panel controls ReviewerFilter shows by default; the admin view adds "userIds"
export const DEFAULT_FILTER_FIELDS = [
  "rating",
  "invoiceStatus",
  "amount",
  "created",
  "opened",
  "reviewChoice",
  "hasComment",
  "sort",
  "includeArchived",
];

export const SORT_OPTIONS = [
  { value: "createdAt", label: "Created date" },
  { value: "amountDue", label: "Amount due" },
  { value: "rating", label: "Rating" },
];

// null means "any" for the tri-state keys (hasComment, opened)
export const createDefaultFilter = () => ({
  q: "",
  ratingMin: null,
  ratingMax: null,
  ratingNone: false,
  hasComment: null,
  invoiceStatus: [],
  amountMin: null,
  amountMax: null,
  createdFrom: null, // "YYYY-MM-DD"
  createdTo: null,
  opened: null,
  reviewChoice: null, // "yes" | "no" | "none"
  includeArchived: false,
  sort: "createdAt",
  dir: "desc",
  // admin only
  userIds: [],
  excludeUserIds: [],
});

const isSet = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  !(Array.isArray(value) && value.length === 0);

/**
 * Converts a filter to query params for api({ query }). Unset keys and
 * defaults are left out, so an empty filter adds nothing to the request.
 * @returns {Object<string, string>}
 */
export const toQueryParams = (filter) => {
  const params = {};
  const add = (key, value) => {
    if (isSet(value)) params[key] = Array.isArray(value) ? value.join(",") : String(value);
  };

  add("q", filter.q?.trim());
  add("ratingMin", filter.ratingMin);
  add("ratingMax", filter.ratingMax);
  if (filter.ratingNone) add("ratingNone", true);
  add("hasComment", filter.hasComment);
  add("invoiceStatus", filter.invoiceStatus);
  add("amountMin", filter.amountMin);
  add("amountMax", filter.amountMax);
  add("createdFrom", filter.createdFrom);
  add("createdTo", filter.createdTo);
  add("opened", filter.opened);
  add("reviewChoice", filter.reviewChoice);
  if (filter.includeArchived) add("includeArchived", true);
  if (filter.sort && filter.sort !== "createdAt") add("sort", filter.sort);
  if (filter.dir && filter.dir !== "desc") add("dir", filter.dir);
  add("userIds", filter.userIds);
  add("excludeUserIds", filter.excludeUserIds);

  return params;
};

const formatDay = (day) =>
  new Date(`${day}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const formatMoney = (value) =>
  Number(value).toLocaleString(undefined, { style: "currency", currency: "USD" });

const ratingLabel = (filter) => {
  const { ratingMin: min, ratingMax: max, ratingNone } = filter;
  let range = null;
  if (min !== null && max !== null) range = min === max ? `Rating ${min}` : `Rating ${min}–${max}`;
  else if (min !== null) range = `Rating ≥ ${min}`;
  else if (max !== null) range = `Rating ≤ ${max}`;
  if (range && ratingNone) return `${range} or not rated`;
  if (ratingNone) return "Not rated yet";
  return range;
};

const rangeLabel = (prefix, min, max, format) => {
  if (isSet(min) && isSet(max)) return `${prefix} ${format(min)} – ${format(max)}`;
  if (isSet(min)) return `${prefix} from ${format(min)}`;
  if (isSet(max)) return `${prefix} until ${format(max)}`;
  return null;
};

/**
 * Active filters for display as removable chips. Each chip's key can be
 * passed to clearFilterKey. Sort order isn't a filter, so it has no chip.
 * @param {Object} filter
 * @param {{userOptions?: Array<{value: string, label: string}>}} [options]
 * @returns {Array<{key: string, label: string}>}
 */
export const activeFilters = (filter, { userOptions = [] } = {}) => {
  const chips = [];
  const push = (key, label) => label && chips.push({ key, label });

  push("q", filter.q?.trim() && `Search: “${filter.q.trim()}”`);
  push("rating", ratingLabel(filter));
  if (filter.hasComment !== null) {
    push("hasComment", filter.hasComment ? "Has comment" : "No comment");
  }
  if (filter.invoiceStatus.length) {
    const labels = filter.invoiceStatus.map(
      (s) => INVOICE_STATUS_OPTIONS.find((o) => o.value === s)?.label || s
    );
    push("invoiceStatus", labels.join(", "));
  }
  push("amount", rangeLabel("Amount", filter.amountMin, filter.amountMax, formatMoney));
  push("created", rangeLabel("Created", filter.createdFrom, filter.createdTo, formatDay));
  if (filter.opened !== null) {
    push("opened", filter.opened ? "Opened link" : "Never opened");
  }
  if (filter.reviewChoice) {
    const labels = { yes: "Chose public review", no: "Declined public review", none: "No review choice" };
    push("reviewChoice", labels[filter.reviewChoice]);
  }
  push("includeArchived", filter.includeArchived && "Including archived");
  if (filter.userIds.length) {
    const labels = filter.userIds.map(
      (id) => userOptions.find((o) => o.value === id)?.label || id.slice(-6)
    );
    push("userIds", `Client: ${labels.join(", ")}`);
  }

  return chips;
};

// Chip keys that cover more than one filter key
const KEY_GROUPS = {
  rating: ["ratingMin", "ratingMax", "ratingNone"],
  amount: ["amountMin", "amountMax"],
  created: ["createdFrom", "createdTo"],
};

/** Resets one chip's keys to their defaults, in place. */
export const clearFilterKey = (filter, key) => {
  const defaults = createDefaultFilter();
  for (const k of KEY_GROUPS[key] || [key]) filter[k] = defaults[k];
};

/**
 * Resets every filter key to its default, in place. Sort order and any keys
 * listed in `keep` are left alone.
 */
export const resetFilter = (filter, { keep = [] } = {}) => {
  const defaults = createDefaultFilter();
  const preserved = new Set(["sort", "dir", ...keep]);
  for (const key of Object.keys(defaults)) {
    if (!preserved.has(key)) filter[key] = defaults[key];
  }
};

export const cloneFilter = (filter) => ({
  ...filter,
  invoiceStatus: [...filter.invoiceStatus],
  userIds: [...filter.userIds],
  excludeUserIds: [...filter.excludeUserIds],
});
